import { Button, PlainButton, Icon } from '@customerio/pluma-components/react';
import { isSessionRunning } from '@customerio/hydra-copilot';
import type { CopilotSession, CopilotSessionStatus, RegistryEntry } from '@customerio/hydra-copilot';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { AnimatePresence, m } from 'motion/react';
import clsx from 'clsx';
import { useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { isEmptyString } from '@/utils/string';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useIsAgentLauncherSuppressed } from '../../hooks/use-suppress-agent-launcher';
import { useCopilotSidebar } from '../../hooks/use-copilot-sidebar';
import { useAgentConversations, type AgentConversationRow } from '../../hooks/use-agent-conversations';
import { useCopilotConversations, useCopilotSessionContext } from '../session-provider';
import { AskAgentSparkleIcon } from '../ask-agent-sparkle-icon';
import { SpellOutText } from './spell-out-text';
import { selectChipRows } from './chip-rows';
import * as styles from './index.css';

/**
 * Closed chips outlive a refresh. A closed conversation comes back only if it
 * starts working again. Keyed per account: session ids are per account, so an
 * unscoped list would close a same-numbered chip in another account.
 */
const CHIP_STORAGE_KEY = 'cio-agent-bar-closed-chips';

/**
 * Unread replies, by conversation id. The live map is keyed by session object,
 * which a reload throws away, so a waiting reply is mirrored here to survive
 * one. Values are the epoch ms the reply landed. Keyed per account like the
 * closed list.
 */
const UNREAD_STORAGE_KEY = 'cio-agent-bar-unread';

function accountStorageKey(key: string, accountId: number | string) {
  return `${key}:${accountId}`;
}

/**
 * The bar carries the most recent few conversations; the agent page lists them
 * all. An older one that is working or has an unread reply keeps its chip past
 * the cap. Closing a chip leaves a gap rather than pulling the next one up: the
 * count is a window on recency, not a slot machine.
 */
const CHIP_LIMIT = 3;

/**
 * Ceiling on the persisted closed and unread maps. The bar only ever looks at
 * the most recent conversations, so anything older than the newest few dozen
 * entries can never match a row again and is dropped on write.
 */
const STORED_IDS_LIMIT = 30;

function capStoredUnread(entries: Record<string, number>): Record<string, number> {
  const ids = Object.keys(entries);
  if (ids.length <= STORED_IDS_LIMIT) return entries;
  const kept = ids.sort((a, b) => (entries[b] ?? 0) - (entries[a] ?? 0)).slice(0, STORED_IDS_LIMIT);
  return Object.fromEntries(kept.map((id) => [id, entries[id] ?? 0]));
}

const CHIP_TRANSITION = { duration: 0.2, ease: [0.2, 0, 0.38, 0.9] } as const;

/** Live status per registered session, updated on any session's transition. */
function useSessionStatuses(entries: ReadonlyArray<RegistryEntry>) {
  const [statuses, setStatuses] = useState<ReadonlyMap<CopilotSession, CopilotSessionStatus>>(new Map());

  useEffect(() => {
    const recompute = () => {
      setStatuses(new Map(entries.map((entry) => [entry.session, entry.session.__status.get()])));
    };
    recompute();
    const unsubscribes = entries.map((entry) => entry.session.__status.listen(recompute));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  return statuses;
}

/**
 * Replies that finished (running → ready) and that the user hasn't opened
 * yet. `viewedSession` is the conversation the user is currently reading (in
 * the open copilot sidebar or the fullscreen agent view, null otherwise): its
 * replies arrive read, while a background conversation's reply is unseen even
 * while the bar itself is hidden.
 * Two levels of acknowledgement: `acknowledgePill` quiets the pill's
 * announcement (any interaction), `clearUnread` clears a conversation's
 * unread state (opening that conversation).
 */
function useUnseenReplies(entries: ReadonlyArray<RegistryEntry>, viewedSession: CopilotSession | null) {
  // Session → epoch ms of its latest unseen reply.
  const [unreadBySession, setUnreadBySession] = useState<ReadonlyMap<CopilotSession, number>>(new Map());
  const [pillSession, setPillSession] = useState<CopilotSession | null>(null);
  const previousStatuses = useRef(new Map<string, CopilotSessionStatus>());
  const viewedSessionRef = useRef(viewedSession);
  viewedSessionRef.current = viewedSession;

  useEffect(() => {
    // Prune state for sessions the registry evicted, so we don't pin them.
    const liveSessions = new Set<CopilotSession>(entries.map((entry) => entry.session));
    setUnreadBySession((current) => {
      if ([...current.keys()].every((session) => liveSessions.has(session))) return current;
      return new Map([...current].filter(([session]) => liveSessions.has(session)));
    });
    setPillSession((current) => (current != null && !liveSessions.has(current) ? null : current));
    const liveKeys = new Set(entries.map((entry) => entry.key));
    for (const key of [...previousStatuses.current.keys()]) {
      if (!liveKeys.has(key)) previousStatuses.current.delete(key);
    }

    const unsubscribes = entries.map((entry) => {
      if (!previousStatuses.current.has(entry.key)) {
        previousStatuses.current.set(entry.key, entry.session.__status.get());
      }
      return entry.session.__status.listen((status) => {
        const previous = previousStatuses.current.get(entry.key);
        previousStatuses.current.set(entry.key, status);
        if (
          previous != null &&
          isSessionRunning(previous) &&
          status === 'ready' &&
          entry.session !== viewedSessionRef.current
        ) {
          setUnreadBySession((current) => {
            const next = new Map(current);
            next.set(entry.session, Date.now());
            return next;
          });
          setPillSession(entry.session);
        }
      });
    });

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  const clearUnread = useCallback((session: CopilotSession) => {
    setUnreadBySession((current) => {
      if (!current.has(session)) return current;
      const next = new Map(current);
      next.delete(session);
      return next;
    });
    setPillSession((current) => (current === session ? null : current));
  }, []);
  const acknowledgePill = useCallback(() => setPillSession(null), []);

  return { unreadBySession, pillSession, acknowledgePill, clearUnread };
}

function isWorkingStatus(status: CopilotSessionStatus | undefined) {
  return isSessionRunning(status) || status === 'initializing';
}

function rowLabelText(row: AgentConversationRow) {
  return !isEmptyString(row.name) ? row.name : `Chat ${row.id}`;
}

type InflightChipProps = {
  row: AgentConversationRow;
  status: CopilotSessionStatus | undefined;
  isUnread: boolean;
  onSelect: (row: AgentConversationRow) => void;
  /** Clears the waiting reply without opening the sidebar. */
  onDismiss: (row: AgentConversationRow) => void;
};

/** A running or unread conversation, parked inline next to the button. */
function InflightChip({ row, status, isUnread, onSelect, onDismiss }: InflightChipProps) {
  const isBusy = isWorkingStatus(status);
  const name = rowLabelText(row);
  // A working chip already wears the gradient ring and shimmer, so a status dot
  // would repeat it visually, but the ring says nothing to a screen reader, so
  // the state lives in the accessible name instead.
  const accessibleName = isBusy ? `${name}: the Agent is working` : isUnread ? `${name}: the Agent replied` : name;
  // The dot means "unread"; the X means "closeable". A chip stays after its
  // reply is read, so it needs the X even once the dot has gone. Only a chip
  // still doing work can't be closed.
  const isDismissable = !isBusy;
  const hasUnreadDot = isUnread && !isBusy;

  return (
    <span
      className={clsx(styles.chipRoot, styles.chip, isBusy && styles.chipWorking)}
      data-unread={hasUnreadDot ? 'true' : undefined}
    >
      <PlainButton
        className={styles.chipMain}
        aria-label={accessibleName}
        data-working={isBusy ? 'true' : undefined}
        data-unread={hasUnreadDot ? 'true' : undefined}
        onClick={() => onSelect(row)}
      >
        {hasUnreadDot ? (
          <span className={styles.chipIndicator}>
            <span className={styles.unreadDot} role="img" aria-label="New reply" />
          </span>
        ) : null}
        <span className={styles.chipLabel}>
          <SpellOutText text={name} />
        </span>
      </PlainButton>
      {isDismissable ? (
        <PlainButton
          className={styles.chipDismiss}
          aria-label={`Remove ${name} from the bar`}
          onClick={() => onDismiss(row)}
        >
          <Icon name="close" size="sm" color="primary" />
        </PlainButton>
      ) : null}
    </span>
  );
}

/** Fade width on whichever edge still has chips hidden behind it. */
const CHIP_FADE_WIDTH = themeVars.space['300'];

/**
 * Makes the hidden-scrollbar chip row usable: maps a vertical wheel onto
 * horizontal scroll (a plain mouse has no horizontal gesture), and reports
 * which edges still have chips behind them so they can fade rather than being
 * sliced mid-word.
 */
function useChipOverflow(element: HTMLDivElement | null, chipsKey: string) {
  const [fade, setFade] = useState({ start: false, end: false });
  const syncRef = useRef(() => {});

  // Keyed on the element, not run once: the bar unmounts its scroller whenever
  // it hides, so the listeners follow whichever scroller is currently rendered.
  useEffect(() => {
    if (element == null) {
      syncRef.current = () => {};
      return;
    }

    const syncFade = () => {
      const start = element.scrollLeft > 1;
      const end = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
      // Guarded: scroll and resize fire far more often than the state changes.
      setFade((previous) => (previous.start === start && previous.end === end ? previous : { start, end }));
    };
    syncRef.current = syncFade;

    const onWheel = (event: WheelEvent) => {
      if (element.scrollWidth <= element.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      element.scrollLeft += event.deltaY;
      event.preventDefault();
    };

    syncFade();
    element.addEventListener('scroll', syncFade, { passive: true });
    // Not passive: preventDefault stops the page scrolling as well.
    element.addEventListener('wheel', onWheel, { passive: false });
    // Catches the bar resizing with the window.
    const observer = new ResizeObserver(syncFade);
    observer.observe(element);

    return () => {
      element.removeEventListener('scroll', syncFade);
      element.removeEventListener('wheel', onWheel);
      observer.disconnect();
    };
  }, [element]);

  // A chip arriving or leaving changes `scrollWidth` without resizing the
  // scroller, so neither the scroll nor the resize listener fires. Re-check
  // explicitly: once immediately, and again once the enter animation has landed.
  useEffect(() => {
    const frame = requestAnimationFrame(() => syncRef.current());
    const settled = setTimeout(() => syncRef.current(), 300);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settled);
    };
  }, [chipsKey, element]);

  return assignInlineVars({
    [styles.chipFadeStart]: fade.start ? CHIP_FADE_WIDTH : '0px',
    [styles.chipFadeEnd]: fade.end ? CHIP_FADE_WIDTH : '0px',
  });
}

type RenderedChip = {
  row: AgentConversationRow;
  status: CopilotSessionStatus | undefined;
  isUnread: boolean;
};

/**
 * Scrolls the chip row to its right end when a conversation starts working, so
 * the chip that just appeared is the one in view. Working chips sort rightmost,
 * which is exactly where an overflowing row hides them.
 */
function useScrollToNewWorkingChip(element: HTMLDivElement | null, workingKey: string) {
  const seenKey = useRef(workingKey);

  useEffect(() => {
    const previousIds = new Set(seenKey.current.split(',').filter((id) => id !== ''));
    const currentIds = workingKey.split(',').filter((id) => id !== '');
    seenKey.current = workingKey;
    if (!currentIds.some((id) => !previousIds.has(id))) return;
    if (element == null) return;
    // The chip animates in, so its width only lands on the next frame.
    const frame = requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      element.scrollTo({ left: element.scrollWidth, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [workingKey, element]);
}

/** Chips doing work sort to the right, nearest the button. */
function isChipWorking(chip: RenderedChip) {
  return isWorkingStatus(chip.status);
}

/** The button copy never varies; working and unread state live on the chips. */
const ASK_AGENT_LABEL = 'Ask the Agent';

type AskAgentButtonProps = {
  accessibleLabel: string;
  onClick: () => void;
};

/**
 * The agent entry point: the flag-off footer's ring-wrapped button, with the
 * ring held on so the bar's one way into the agent reads before any hover.
 */
function AskAgentButton({ accessibleLabel, onClick }: AskAgentButtonProps) {
  return (
    <GradientRing radius={4} isAlwaysActive>
      <Button
        variant="secondary"
        size="sm"
        aria-label={accessibleLabel}
        className={gradientHoverTarget}
        onClick={onClick}
      >
        <AskAgentSparkleIcon />
        {ASK_AGENT_LABEL}
      </Button>
    </GradientRing>
  );
}

export function AgentFooterBar() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const isHistorySidebarOpen = useSidebarActive('copilot-history');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const isSuppressed = useIsAgentLauncherSuppressed();
  const { track } = useAnalytics();
  const auth = useAuth();
  const { openCopilot } = useCopilotSidebar();
  const themeContext = useThemeContext();
  const session = useCopilotSessionContext();
  const { newConversation, switchToConversation, entries } = useCopilotConversations();
  // From history as well as this tab's live sessions, newest first.
  const { rows: conversations } = useAgentConversations();
  const statuses = useSessionStatuses(entries);
  const isHidden = isCopilotSidebarOpen || isHistorySidebarOpen || !shouldShowAgentBar || isSuppressed;
  // The conversation the user is currently reading: the active session while
  // the copilot sidebar or the fullscreen agent view shows it.
  const viewedSession = isCopilotSidebarOpen || !shouldShowAgentBar ? session : null;
  const { unreadBySession, pillSession, acknowledgePill, clearUnread } = useUnseenReplies(entries, viewedSession);

  // Pages under the bar offset their own bottom chrome by this variable, the
  // same contract the legacy assistant footer keeps.
  useEffect(() => {
    themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, isHidden ? '0px' : styles.BAR_RESERVED_HEIGHT);

    return () => {
      themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, '0px');
    };
  }, [isHidden, themeContext]);

  // State rather than a ref: the scroller div comes and goes with the bar's
  // visibility, and the hooks below rebind to whichever one is mounted.
  const [chipScroller, setChipScroller] = useState<HTMLDivElement | null>(null);
  // Persisted, so closing a chip sticks across a refresh.
  const [closedChipIds, setClosedChipIds] = useLocalStorage<readonly string[]>(
    accountStorageKey(CHIP_STORAGE_KEY, auth.account.id),
    [],
  );
  const [unreadAtById, setUnreadAtById] = useLocalStorage<Record<string, number>>(
    accountStorageKey(UNREAD_STORAGE_KEY, auth.account.id),
    {},
  );
  const isRowWorking = (row: AgentConversationRow) =>
    row.liveSession != null && isWorkingStatus(statuses.get(row.liveSession));
  const isRowUnread = (row: AgentConversationRow) =>
    (row.liveSession != null && unreadBySession.has(row.liveSession)) || unreadAtById[row.id] != null;
  // Each of these gets a chip until the user closes it.
  const rows = selectChipRows(conversations, CHIP_LIMIT, (row) => isRowWorking(row) || isRowUnread(row));
  // Mirror the live unread map into storage as replies land.
  const liveUnreadKey = [...unreadBySession.entries()].map(([session, at]) => `${session.id ?? ''}:${at}`).join(',');
  useEffect(() => {
    setUnreadAtById((current) => {
      let next = current;
      for (const [session, at] of unreadBySession) {
        const id = session.id;
        if (id != null && current[id] !== at) {
          if (next === current) next = { ...current };
          next[id] = at;
        }
      }
      return next === current ? current : capStoredUnread(next);
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id:timestamp pairs, not the map identity
  }, [liveUnreadKey]);

  const forgetUnread = useCallback(
    (id: string) => {
      setUnreadAtById((current) => {
        if (current[id] == null) return current;
        const next = { ...current };
        delete next[id];
        return next;
      });
    },
    [setUnreadAtById],
  );
  // Reading a conversation anywhere counts: opened from the history sidebar,
  // the chat status or the agent page, its dot goes too, not only on a chip
  // click. `id` is read once here; a viewed conversation already has one.
  const viewedId = viewedSession?.id;
  useEffect(() => {
    if (viewedSession == null || viewedId == null) return;
    clearUnread(viewedSession);
    forgetUnread(viewedId);
  }, [viewedSession, viewedId, clearUnread, forgetUnread]);
  const visibleChipKey = rows
    .filter((row) => !closedChipIds.includes(row.id))
    .map((row) => row.id)
    .join(',');
  const chipFadeStyle = useChipOverflow(chipScroller, visibleChipKey);
  const workingChipKey = rows
    .filter(isRowWorking)
    .map((row) => row.id)
    .join(',');
  useScrollToNewWorkingChip(chipScroller, workingChipKey);
  // A closed conversation that starts working again is the one thing the user
  // most needs to see, so it earns its chip back.
  useEffect(() => {
    const workingIds = workingChipKey.split(',').filter((id) => id !== '');
    if (!workingIds.some((id) => closedChipIds.includes(id))) return;
    setClosedChipIds((current) => current.filter((id) => !workingIds.includes(id)));
  }, [workingChipKey, closedChipIds, setClosedChipIds]);

  const hasUnseenReply = pillSession != null;
  // Visible copy never changes, so the accessible name carries the one thing a
  // screen reader can't get from the chip's outline: that a reply is waiting. It
  // keeps the visible label as its prefix, satisfying WCAG 2.5.3.
  const accessibleLabel = hasUnseenReply ? `${ASK_AGENT_LABEL}: the Agent replied` : ASK_AGENT_LABEL;

  if (isHidden) {
    return null;
  }

  // A recent chip stays put once it has appeared, even after its reply is read;
  // only the X takes it away. One past the cap goes once its news is read.
  // Ordered by when each first showed up.
  // `rows` arrives newest-first; reversed so a new conversation appears against
  // the button and older ones are pushed left, rather than the other way round.
  const chipRows = rows.filter((row) => !closedChipIds.includes(row.id)).reverse();
  const renderedChips: RenderedChip[] = chipRows
    .map((row) => ({
      row,
      status: row.liveSession != null ? statuses.get(row.liveSession) : undefined,
      isUnread: isRowUnread(row),
    }))
    // Working chips end up rightmost, so the ones doing something sit against
    // the button. `sort` is stable, so each group keeps its own order.
    .sort((a, b) => Number(isChipWorking(a)) - Number(isChipWorking(b)));

  const handleOpen = () => {
    acknowledgePill();
    // The button always starts a fresh conversation; existing ones are reached
    // through their chip or the history sidebar.
    track('aiAssistant', 'newChatClicked', { 'Click Location': 'bar' });
    newConversation();
    openCopilot({ triggerSource: 'bar' });
  };

  /** Takes the chip off the bar without opening the sidebar. The conversation
   * itself is untouched and stays in the agent page's history. */
  const handleDismissChip = (row: AgentConversationRow) => {
    if (row.liveSession != null) clearUnread(row.liveSession);
    forgetUnread(row.id);
    // Newest last; the oldest fall off the front once past the ceiling.
    setClosedChipIds((current) => (current.includes(row.id) ? current : [...current, row.id].slice(-STORED_IDS_LIMIT)));
  };

  const handleSelectConversation = (row: AgentConversationRow, clickLocation: 'bar' | 'bar_chip') => {
    track('aiAssistant', 'historyItemClicked', { 'Click Location': clickLocation });
    const opened = switchToConversation(row.id);
    clearUnread(opened);
    forgetUnread(row.id);
    openCopilot({ triggerSource: clickLocation === 'bar_chip' ? 'bar_chip' : 'bar_conversation' });
  };

  return (
    <div className={styles.barBleed}>
      <div className={styles.bar}>
        <div className={styles.actionsContainer}>
          <div className={styles.chipScroller} ref={setChipScroller} style={chipFadeStyle}>
            <AnimatePresence mode="popLayout">
              {renderedChips.map(({ row, status, isUnread }) => (
                <m.div
                  key={row.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={CHIP_TRANSITION}
                >
                  <InflightChip
                    row={row}
                    status={status}
                    isUnread={isUnread}
                    onSelect={(selected) => handleSelectConversation(selected, 'bar_chip')}
                    onDismiss={handleDismissChip}
                  />
                </m.div>
              ))}
            </AnimatePresence>
          </div>
          <AskAgentButton accessibleLabel={accessibleLabel} onClick={handleOpen} />
        </div>
      </div>
    </div>
  );
}
